<template>
  <div class="w-full">
    <label
      v-if="label"
      :for="inputId"
      class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
    >
      {{ label }}
    </label>
    <div class="flex">
      <div class="relative w-full">
        <input
          :id="inputId"
          :type="inputType"
          class="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 border border-gray-300 rounded-s-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
          :class="{ 'pr-10': masked && modelValue }"
          :placeholder="placeholder"
          :value="modelValue"
          :disabled="disabled || (locked && validated)"
          @input="onInput"
        />
        <button
          v-if="masked && modelValue"
          type="button"
          class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
          tabindex="-1"
          @click="showPassword = !showPassword"
        >
          <!-- Eye icon (shown when password is hidden) -->
          <svg
            v-if="!showPassword"
            class="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
            />
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          <!-- Eye-slash icon (shown when password is visible) -->
          <svg
            v-else
            class="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M3.98 8.223A10.477 10.477 0 001.934 12c1.292 4.338 5.31 7.5 10.066 7.5.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
            />
          </svg>
        </button>
      </div>
      <button
        v-if="cancellable && !validated"
        type="button"
        class="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:ring-4 focus:outline-none focus:ring-blue-300 whitespace-nowrap"
        @click="$emit('cancel')"
      >
        Cancel
      </button>
      <button
        v-if="!validated"
        type="button"
        class="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-e-lg hover:bg-blue-700 focus:ring-4 focus:outline-none focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 whitespace-nowrap"
        :class="{ 'opacity-50 cursor-not-allowed': buttonDisabled }"
        :disabled="buttonDisabled"
        @click="$emit('action')"
      >
        <svg
          v-if="loading"
          class="inline w-4 h-4 animate-spin"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.59c0 27.61-22.39 50-50 50S0 78.2 0 50.59h10c0 22.09 17.91 40 40 40s40-17.91 40-40-17.91-40-40-40V.59c27.61 0 50 22.39 50 50z"
            fill="currentColor"
          />
        </svg>
        <span v-else>{{ buttonText }}</span>
      </button>
      <button
        v-else-if="locked"
        type="button"
        class="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-e-lg hover:bg-gray-50 focus:ring-4 focus:outline-none focus:ring-blue-300 whitespace-nowrap"
        @click="$emit('unlock')"
      >
        Edit
      </button>
      <div
        v-else
        class="flex items-center justify-center px-4 py-2.5 border border-green-500 bg-green-50 rounded-e-lg dark:bg-green-900 dark:border-green-600"
      >
        <svg
          class="w-5 h-5 text-green-600 dark:text-green-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>
    </div>
    <p v-if="helperText" class="mt-2 text-sm" :class="helperTextClass">
      {{ helperText }}
    </p>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";

const props = withDefaults(
  defineProps<{
    modelValue: string | null;
    label?: string;
    placeholder?: string;
    buttonText?: string;
    inputId?: string;
    disabled?: boolean;
    loading?: boolean;
    validated?: boolean;
    masked?: boolean;
    locked?: boolean;
    cancellable?: boolean;
    helperText?: string;
    validation?: "success" | "error" | "";
  }>(),
  {
    label: "",
    placeholder: "",
    buttonText: "Submit",
    inputId: "action-input",
    disabled: false,
    loading: false,
    validated: false,
    masked: false,
    locked: false,
    cancellable: false,
    helperText: "",
    validation: ""
  }
);

const emit = defineEmits<{
  "update:modelValue": [value: string];
  action: [];
  unlock: [];
  cancel: [];
}>();

const showPassword = ref(false);

const inputType = computed(() => {
  if (props.masked && !showPassword.value) return "password";
  return "text";
});

const buttonDisabled = computed(
  () => props.disabled || props.loading || !props.modelValue
);

const helperTextClass = computed(() => {
  if (props.validation === "success")
    return "text-green-600 dark:text-green-500";
  if (props.validation === "error") return "text-red-600 dark:text-red-500";
  return "text-gray-500 dark:text-gray-400";
});

function onInput(event: Event) {
  emit("update:modelValue", (event.target as HTMLInputElement).value);
}
</script>
